import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import PageShell from "@/components/PageShell";
import api from "@/lib/api";
import { Scale, Search, ExternalLink, Landmark, BookText, Banknote, Gavel, Bookmark, BookmarkCheck, Receipt } from "lucide-react";
import { toast } from "sonner";
import { ReadAloud } from "@/components/ReadAloud";

const CAT_ICON = { acts: Landmark, usc: BookText, ucc: Banknote, frcp: Gavel, irs: Receipt };

export default function StatuteLibrary() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [data, setData] = useState({ categories: [], items: [] });
  const [q, setQ] = useState(searchParams.get("q") || "");
  const [cat, setCat] = useState(searchParams.get("category") || "all");
  const [saved, setSaved] = useState(new Set());

  useEffect(() => {
    api.get("/statutes").then((r) => setData(r.data)).catch(() => {});
    api.get("/bookmarks").then((r) => setSaved(new Set(r.data.filter((b) => b.statute_id).map((b) => b.statute_id)))).catch(() => {});
  }, []);

  const saveStatute = async (s) => {
    try {
      await api.post("/bookmarks", { kind: "statute", title: s.title, citation: s.citation, url: s.url, statute_id: s.id, content: s.summary });
      setSaved((prev) => new Set(prev).add(s.id));
      toast.success("Saved to Research folder");
    } catch { toast.error("Could not save"); }
  };

  const saveIndexItem = async (item) => {
    try {
      await api.post("/bookmarks", { kind: "statute", title: item.title, citation: item.citation, url: item.url, statute_id: item.sid, content: item.content });
      setSaved((prev) => new Set(prev).add(item.sid));
      toast.success("Saved to Research folder");
    } catch { toast.error("Could not save"); }
  };

  useEffect(() => {
    api.get("/statutes").then((r) => setData(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    const next = {};
    if (q) next.q = q;
    if (cat !== "all") next.category = cat;
    setSearchParams(next, { replace: true });
  }, [q, cat, setSearchParams]);

  const filtered = useMemo(() => {
    const ql = q.trim().toLowerCase();
    return data.items.filter((s) => {
      if (cat !== "all" && s.category !== cat) return false;
      if (!ql) return true;
      return s.title.toLowerCase().includes(ql) || s.summary.toLowerCase().includes(ql) || s.citation.toLowerCase().includes(ql);
    });
  }, [data, q, cat]);

  const grouped = useMemo(() => {
    const map = {};
    filtered.forEach((s) => { (map[s.category] = map[s.category] || []).push(s); });
    return map;
  }, [filtered]);

  const uscTitles = useMemo(() => {
    const ql = q.trim().toLowerCase();
    return (data.usc_titles || []).filter((t) => {
      if (cat !== "all" && cat !== "usc") return false;
      if (!ql) return true;
      return String(t.number) === ql || t.name.toLowerCase().includes(ql) || `${t.number} u.s.c.`.includes(ql) || `title ${t.number}`.includes(ql);
    });
  }, [data, q, cat]);

  const uccArticles = useMemo(() => {
    const ql = q.trim().toLowerCase();
    return (data.ucc_articles || []).filter((a) => {
      if (cat !== "all" && cat !== "ucc") return false;
      if (!ql) return true;
      return a.article.toLowerCase() === ql || a.name.toLowerCase().includes(ql) || `article ${a.article}`.toLowerCase().includes(ql) || `u.c.c. art. ${a.article}`.toLowerCase().includes(ql);
    });
  }, [data, q, cat]);

  const frcpRules = useMemo(() => {
    const ql = q.trim().toLowerCase();
    return (data.frcp_rules || []).filter((r) => {
      if (cat !== "all" && cat !== "frcp") return false;
      if (!ql) return true;
      return r.rule.toLowerCase() === ql || r.name.toLowerCase().includes(ql) || `rule ${r.rule}`.includes(ql) || (r.citation || "").toLowerCase().includes(ql) || (r.title || "").toLowerCase().includes(ql);
    });
  }, [data, q, cat]);

  const ircSections = useMemo(() => {
    const ql = q.trim().toLowerCase();
    return (data.irc_sections || []).filter((s) => {
      if (cat !== "all" && cat !== "irs") return false;
      if (!ql) return true;
      return s.section.toLowerCase().includes(ql) || s.name.toLowerCase().includes(ql) || "title 26 internal revenue code".includes(ql);
    });
  }, [data, q, cat]);

  const irsForms = useMemo(() => {
    const ql = q.trim().toLowerCase();
    return (data.irs_forms || []).filter((f) => {
      if (cat !== "all" && cat !== "irs") return false;
      if (!ql) return true;
      return f.number.toLowerCase().includes(ql) || f.name.toLowerCase().includes(ql) || "irs form".includes(ql);
    });
  }, [data, q, cat]);

  const nothing = filtered.length === 0 && uscTitles.length === 0 && uccArticles.length === 0 && frcpRules.length === 0 && ircSections.length === 0 && irsForms.length === 0;

  const catLabel = (key) => (data.categories.find((c) => c.key === key) || {}).label || key;

  return (
    <PageShell>
      <div className="max-w-6xl mx-auto px-6 py-14">
        <span className="text-xs uppercase tracking-widest font-mono text-primary">Reference</span>
        <div className="flex items-center gap-3 mt-2 mb-3">
          <Scale className="w-7 h-7 text-primary" />
          <h1 className="font-serif text-5xl">Statute Library</h1>
        </div>
        <p className="text-muted-foreground max-w-2xl mb-8">
          The Major Acts of Congress, key sections of the U.S. Code, the Uniform Commercial Code, and the
          Federal Rules of Civil Procedure your courses cite — each with a plain-English summary. Read the full,
          authoritative text on the{" "}
          <span className="text-foreground">Cornell Legal Information Institute</span>.
        </p>

        <div className="flex flex-col sm:flex-row gap-3 mb-6">
          <div className="relative flex-1">
            <Search className="w-4 h-4 text-muted-foreground absolute left-3 top-1/2 -translate-y-1/2" />
            <input
              data-testid="statute-search"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search statutes, citations, topics…"
              className="w-full bg-muted border border-input pl-9 pr-3 py-2.5 rounded-sm outline-none focus:border-primary text-sm"
            />
          </div>
        </div>

        <div className="flex flex-wrap gap-2 mb-10">
          <button onClick={() => setCat("all")} data-testid="statute-cat-all"
            className={`px-4 py-2 text-xs uppercase tracking-widest font-mono border transition-colors ${cat === "all" ? "bg-primary text-primary-foreground border-primary" : "bg-card border-border text-muted-foreground hover:border-primary"}`}>
            All
          </button>
          {data.categories.map((c) => (
            <button key={c.key} onClick={() => setCat(c.key)} data-testid={`statute-cat-${c.key}`}
              className={`px-4 py-2 text-xs uppercase tracking-widest font-mono border transition-colors ${cat === c.key ? "bg-primary text-primary-foreground border-primary" : "bg-card border-border text-muted-foreground hover:border-primary"}`}>
              {c.label}
            </button>
          ))}
        </div>

        {(cat === "all" ? data.categories.map((c) => c.key) : [cat]).map((key) =>
          (grouped[key] || []).length ? (
            <section key={key} className="mb-12" data-testid={`statute-section-${key}`}>
              <div className="flex items-center gap-2.5 mb-5 pb-2 border-b border-border">
                {(() => { const I = CAT_ICON[key] || Scale; return <I className="w-5 h-5 text-primary" />; })()}
                <h2 className="font-serif text-2xl">{catLabel(key)}</h2>
                <span className="text-xs font-mono text-muted-foreground">{(grouped[key] || []).length}</span>
              </div>
              <div className="grid gap-4" style={{ gridTemplateColumns: "repeat(auto-fill,minmax(320px,1fr))" }}>
                {grouped[key].map((s) => (
                  <div key={s.id} data-testid={`statute-card-${s.id}`}
                    className="group border border-border bg-card p-5 flex flex-col hover:border-primary transition-colors">
                    <div className="flex items-start justify-between gap-3">
                      <a href={s.url} target="_blank" rel="noreferrer" className="font-serif text-lg leading-tight group-hover:text-primary transition-colors">{s.title}</a>
                      <button onClick={() => saveStatute(s)} disabled={saved.has(s.id)} data-testid={`statute-save-${s.id}`}
                        title={saved.has(s.id) ? "Saved to Research" : "Save to Research folder"}
                        className={`shrink-0 mt-0.5 ${saved.has(s.id) ? "text-primary" : "text-muted-foreground hover:text-primary"}`}>
                        {saved.has(s.id) ? <BookmarkCheck className="w-4 h-4" /> : <Bookmark className="w-4 h-4" />}
                      </button>
                    </div>
                    <span className="mt-1.5 text-[11px] font-mono uppercase tracking-wider text-primary/80 border border-primary/30 self-start px-2 py-0.5 rounded-sm">{s.citation}</span>
                    <p className="text-sm text-muted-foreground leading-relaxed mt-3 flex-1">{s.summary}</p>
                    <div className="mt-4 flex items-center gap-4 self-start">
                      <a href={s.url} target="_blank" rel="noreferrer" className="text-xs font-mono uppercase tracking-widest text-primary inline-flex items-center gap-1.5 hover:underline">
                        Read on Cornell LII <ExternalLink className="w-3 h-3" />
                      </a>
                      <ReadAloud label="Read aloud" getText={() => `${s.title}. ${s.citation}. ${s.summary}`} />
                    </div>
                  </div>
                ))}
              </div>
            </section>
          ) : null
        )}

        {filtered.length === 0 && !nothing && q && (
          <p className="text-muted-foreground text-sm" data-testid="statute-empty-curated">No highlighted statutes match “{q}”. Browse the complete code below.</p>
        )}

        {uscTitles.length > 0 && (
          <section className="mb-14" data-testid="usc-full-index">
            <div className="flex items-center gap-2.5 mb-2 pt-8 border-t border-border">
              <BookText className="w-5 h-5 text-primary" />
              <h2 className="font-serif text-2xl">The Complete U.S. Code</h2>
              <span className="text-xs font-mono text-muted-foreground">{uscTitles.length} titles</span>
            </div>
            <p className="text-sm text-muted-foreground mb-5 max-w-2xl">
              Every one of the 54 titles of the United States Code — tap any title to jump straight to its full,
              authoritative text on the Cornell Legal Information Institute.
            </p>
            <div className="grid gap-2" style={{ gridTemplateColumns: "repeat(auto-fill,minmax(280px,1fr))" }}>
              {uscTitles.map((t) => {
                const sid = `usc-title-${t.number}`;
                return t.reserved ? (
                  <div key={t.number} data-testid={`usc-title-${t.number}`}
                    className="border border-dashed border-border bg-card/50 px-3.5 py-3 flex items-center gap-3 opacity-60">
                    <span className="font-mono text-xs text-primary/70 w-14 shrink-0">Title {t.number}</span>
                    <span className="text-sm text-muted-foreground italic">Reserved</span>
                  </div>
                ) : (
                  <div key={t.number} data-testid={`usc-title-${t.number}`}
                    className="group border border-border bg-card px-3.5 py-3 flex items-center gap-3 hover:border-primary transition-colors">
                    <span className="font-mono text-xs text-primary w-14 shrink-0">Title {t.number}</span>
                    <a href={t.url} target="_blank" rel="noreferrer" className="text-sm leading-tight flex-1 group-hover:text-primary transition-colors inline-flex items-center gap-1.5">
                      {t.name} <ExternalLink className="w-3 h-3 text-muted-foreground group-hover:text-primary shrink-0" />
                    </a>
                    <button onClick={() => saveIndexItem({ sid, title: `Title ${t.number} — ${t.name}`, citation: `${t.number} U.S.C.`, url: t.url, content: `United States Code — Title ${t.number}: ${t.name}` })}
                      disabled={saved.has(sid)} data-testid={`usc-title-save-${t.number}`}
                      title={saved.has(sid) ? "Saved to Research" : "Save to Research folder"}
                      className={`shrink-0 ${saved.has(sid) ? "text-primary" : "text-muted-foreground hover:text-primary"}`}>
                      {saved.has(sid) ? <BookmarkCheck className="w-4 h-4" /> : <Bookmark className="w-4 h-4" />}
                    </button>
                  </div>
                );
              })}
            </div>
          </section>
        )}

        {uccArticles.length > 0 && (
          <section className="mb-14" data-testid="ucc-full-index">
            <div className="flex items-center gap-2.5 mb-2 pt-8 border-t border-border">
              <Banknote className="w-5 h-5 text-primary" />
              <h2 className="font-serif text-2xl">The Complete Uniform Commercial Code</h2>
              <span className="text-xs font-mono text-muted-foreground">{uccArticles.length} articles</span>
            </div>
            <p className="text-sm text-muted-foreground mb-5 max-w-2xl">
              Every article of the UCC — tap any article to read its full official text on the Cornell Legal
              Information Institute.
            </p>
            <div className="grid gap-2" style={{ gridTemplateColumns: "repeat(auto-fill,minmax(300px,1fr))" }}>
              {uccArticles.map((a) => {
                const sid = `ucc-art-${a.article}`;
                return (
                  <div key={a.article} data-testid={`ucc-article-${a.article}`}
                    className="group border border-border bg-card px-3.5 py-3 flex items-center gap-3 hover:border-primary transition-colors">
                    <span className="font-mono text-xs text-primary w-20 shrink-0">Art. {a.article}</span>
                    <a href={a.url} target="_blank" rel="noreferrer" className="text-sm leading-tight flex-1 group-hover:text-primary transition-colors inline-flex items-center gap-1.5">
                      {a.name} <ExternalLink className="w-3 h-3 text-muted-foreground group-hover:text-primary shrink-0" />
                    </a>
                    <button onClick={() => saveIndexItem({ sid, title: `UCC Article ${a.article} — ${a.name}`, citation: `U.C.C. Art. ${a.article}`, url: a.url, content: `Uniform Commercial Code — Article ${a.article}: ${a.name}` })}
                      disabled={saved.has(sid)} data-testid={`ucc-article-save-${a.article}`}
                      title={saved.has(sid) ? "Saved to Research" : "Save to Research folder"}
                      className={`shrink-0 ${saved.has(sid) ? "text-primary" : "text-muted-foreground hover:text-primary"}`}>
                      {saved.has(sid) ? <BookmarkCheck className="w-4 h-4" /> : <Bookmark className="w-4 h-4" />}
                    </button>
                  </div>
                );
              })}
            </div>
          </section>
        )}

        {frcpRules.length > 0 && (
          <section className="mb-14" data-testid="frcp-full-index">
            <div className="flex items-center gap-2.5 mb-2 pt-8 border-t border-border">
              <Gavel className="w-5 h-5 text-primary" />
              <h2 className="font-serif text-2xl">The Federal Rules of Civil Procedure</h2>
              <span className="text-xs font-mono text-muted-foreground">{frcpRules.filter((r) => !r.reserved).length} rules</span>
            </div>
            <p className="text-sm text-muted-foreground mb-5 max-w-2xl">
              The complete rules that govern civil lawsuits in the U.S. federal district courts — tap any rule to
              read its full authoritative text on the Cornell Legal Information Institute.
            </p>
            <div className="grid gap-2" style={{ gridTemplateColumns: "repeat(auto-fill,minmax(320px,1fr))" }}>
              {frcpRules.map((r) => {
                const sid = `frcp-rule-${r.rule}`;
                return r.reserved ? (
                  <div key={r.rule} data-testid={`frcp-rule-${r.rule}`}
                    className="border border-dashed border-border bg-card/50 px-3.5 py-3 flex items-center gap-3 opacity-60">
                    <span className="font-mono text-xs text-primary/70 w-16 shrink-0">Rule {r.rule}</span>
                    <span className="text-sm text-muted-foreground italic">Abrogated</span>
                  </div>
                ) : (
                  <div key={r.rule} data-testid={`frcp-rule-${r.rule}`}
                    className="group border border-border bg-card px-3.5 py-3 flex items-center gap-3 hover:border-primary transition-colors">
                    <span className="font-mono text-xs text-primary w-16 shrink-0">Rule {r.rule}</span>
                    <a href={r.url} target="_blank" rel="noreferrer" className="text-sm leading-tight flex-1 group-hover:text-primary transition-colors inline-flex items-center gap-1.5">
                      {r.name} <ExternalLink className="w-3 h-3 text-muted-foreground group-hover:text-primary shrink-0" />
                    </a>
                    <button onClick={() => saveIndexItem({ sid, title: `FRCP Rule ${r.rule} — ${r.name}`, citation: r.citation, url: r.url, content: `Federal Rules of Civil Procedure — Rule ${r.rule}: ${r.name} (${r.title})` })}
                      disabled={saved.has(sid)} data-testid={`frcp-rule-save-${r.rule}`}
                      title={saved.has(sid) ? "Saved to Research" : "Save to Research folder"}
                      className={`shrink-0 ${saved.has(sid) ? "text-primary" : "text-muted-foreground hover:text-primary"}`}>
                      {saved.has(sid) ? <BookmarkCheck className="w-4 h-4" /> : <Bookmark className="w-4 h-4" />}
                    </button>
                  </div>
                );
              })}
            </div>
          </section>
        )}

        {ircSections.length > 0 && (
          <section className="mb-14" data-testid="irc-full-index">
            <div className="flex items-center gap-2.5 mb-2 pt-8 border-t border-border">
              <Receipt className="w-5 h-5 text-primary" />
              <h2 className="font-serif text-2xl">Internal Revenue Code — Title 26</h2>
              <span className="text-xs font-mono text-muted-foreground">{ircSections.length} entries</span>
            </div>
            <p className="text-sm text-muted-foreground mb-5 max-w-2xl">
              The eleven subtitles of the Internal Revenue Code plus its most-cited sections — tap any entry to
              read its full official text on the Cornell Legal Information Institute. Educational only — not tax advice.
            </p>
            <div className="grid gap-2" style={{ gridTemplateColumns: "repeat(auto-fill,minmax(320px,1fr))" }}>
              {ircSections.map((s) => {
                const sid = `irc-${s.section}`;
                return (
                  <div key={s.section} data-testid={`irc-${s.section}`}
                    className={`group border bg-card px-3.5 py-3 flex items-center gap-3 hover:border-primary transition-colors ${s.kind === "subtitle" ? "border-primary/40" : "border-border"}`}>
                    <span className="font-mono text-xs text-primary w-24 shrink-0">{s.section}</span>
                    <a href={s.url} target="_blank" rel="noreferrer" className="text-sm leading-tight flex-1 group-hover:text-primary transition-colors inline-flex items-center gap-1.5">
                      {s.name} <ExternalLink className="w-3 h-3 text-muted-foreground group-hover:text-primary shrink-0" />
                    </a>
                    <button onClick={() => saveIndexItem({ sid, title: `26 U.S.C. ${s.section} — ${s.name}`, citation: `26 U.S.C. ${s.section}`, url: s.url, content: `Internal Revenue Code (Title 26) ${s.section}: ${s.name}` })}
                      disabled={saved.has(sid)} data-testid={`irc-save-${s.section}`}
                      title={saved.has(sid) ? "Saved to Research" : "Save to Research folder"}
                      className={`shrink-0 ${saved.has(sid) ? "text-primary" : "text-muted-foreground hover:text-primary"}`}>
                      {saved.has(sid) ? <BookmarkCheck className="w-4 h-4" /> : <Bookmark className="w-4 h-4" />}
                    </button>
                  </div>
                );
              })}
            </div>
          </section>
        )}

        {irsForms.length > 0 && (
          <section className="mb-14" data-testid="irs-forms-index">
            <div className="flex items-center gap-2.5 mb-2 pt-8 border-t border-border">
              <Receipt className="w-5 h-5 text-primary" />
              <h2 className="font-serif text-2xl">IRS Forms</h2>
              <span className="text-xs font-mono text-muted-foreground">{irsForms.length} forms</span>
            </div>
            <p className="text-sm text-muted-foreground mb-5 max-w-2xl">
              The most commonly used IRS forms — tap any form to open its official instructions and PDF on IRS.gov.
            </p>
            <div className="grid gap-2" style={{ gridTemplateColumns: "repeat(auto-fill,minmax(320px,1fr))" }}>
              {irsForms.map((f) => {
                const sid = `irs-form-${f.number}`;
                return (
                  <div key={f.number} data-testid={`irs-form-${f.number}`}
                    className="group border border-border bg-card px-3.5 py-3 flex items-center gap-3 hover:border-primary transition-colors">
                    <span className="font-mono text-xs text-primary w-24 shrink-0">{f.number}</span>
                    <a href={f.url} target="_blank" rel="noreferrer" className="text-sm leading-tight flex-1 group-hover:text-primary transition-colors inline-flex items-center gap-1.5">
                      {f.name} <ExternalLink className="w-3 h-3 text-muted-foreground group-hover:text-primary shrink-0" />
                    </a>
                    <button onClick={() => saveIndexItem({ sid, title: `IRS Form ${f.number} — ${f.name}`, citation: `IRS Form ${f.number}`, url: f.url, content: `IRS Form ${f.number}: ${f.name}` })}
                      disabled={saved.has(sid)} data-testid={`irs-form-save-${f.number}`}
                      title={saved.has(sid) ? "Saved to Research" : "Save to Research folder"}
                      className={`shrink-0 ${saved.has(sid) ? "text-primary" : "text-muted-foreground hover:text-primary"}`}>
                      {saved.has(sid) ? <BookmarkCheck className="w-4 h-4" /> : <Bookmark className="w-4 h-4" />}
                    </button>
                  </div>
                );
              })}
            </div>
          </section>
        )}

        {nothing && (
          <p className="text-muted-foreground text-sm" data-testid="statute-empty">No statutes, titles, articles, or rules match “{q}”.</p>
        )}

        <p className="text-xs text-muted-foreground/80 font-mono mt-8 border-t border-border pt-6">
          Full text is hosted by the Cornell Legal Information Institute (law.cornell.edu). Summaries are for
          orientation only and are not legal advice.
        </p>
      </div>
    </PageShell>
  );
}
